import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import utc from 'dayjs/plugin/utc';

import { localeConfig as enLocaleConfig } from './config/en';
import { DEFAULT_THRESHOLDS, LOCALE_NAMES_SET } from './constant';
import customFormat from './lib/customFormat';
import relativeTime from './lib/relativeTime';
import timeExpression from './lib/timeExpression';
import { FormatType, TransformedLocaleConfig, DateOrTimestamp, TimeExpressionOptions } from './type';

export * from './type';

dayjs.extend(utc);
// extend dayjs with advanced format
dayjs.extend(localizedFormat);
// extend dayjs with custom format like 'LL-Y'
dayjs.extend(customFormat);
dayjs.extend(relativeTime, {
  thresholds: DEFAULT_THRESHOLDS,
  rounding: Math.round,
});
dayjs.extend(timeExpression, {
  justNowThreshold: 1000,
});
dayjs.locale(enLocaleConfig.name, enLocaleConfig);

/** parse raw locale to supported locale */
export function parseLocale(raw: string): string {
  const lowerCased = raw.toLowerCase();
  if (lowerCased.startsWith('zh-hant')) {
    return 'zh-Hant';
  }

  if (lowerCased === 'zh-hans') {
    return 'zh-Hans';
  }

  //https://bytedance.feishu.cn/docx/doxcntsCKOW6tfF1grIkKjSBmwh
  if (lowerCased.startsWith('fr-ca')) {
    return 'fr-ca';
  }

  const language = lowerCased.split('-')[0];
  if (LOCALE_NAMES_SET.has(language)) {
    return language;
  }

  return 'en';
}

export class BDDateFmt {
  currentLocale: string = enLocaleConfig.name;

  get currentLocaleConfig(): TransformedLocaleConfig {
    this.protectMultiThreadedRender();
    return dayjs().$locale();
  }

  /** return formatted string */
  format(dateOrTimestamp: DateOrTimestamp, type: FormatType): string {
    this.protectMultiThreadedRender();
    const date = this.getDate(dateOrTimestamp);
    return dayjs(date).format(type);
  }

  /** switch locale */
  setLocale(locale: string): void {
    this.currentLocale = parseLocale(locale);
    dayjs.locale(parseLocale(locale));
  }

  /** load locale config from CDN and set that locale */
  // loadLocale(locale: string): Promise<void> {}

  /* manually set and use LocaleConfig */
  setLocaleConfig(localeConfig: TransformedLocaleConfig): BDDateFmt {
    this.currentLocale = parseLocale(localeConfig.name);
    dayjs.locale(localeConfig.name, localeConfig);
    return this;
  }

  utcFormat(dateOrTimestamp: DateOrTimestamp, type: FormatType): string {
    this.protectMultiThreadedRender();
    const date = this.getDate(dateOrTimestamp);
    return dayjs(date).utc().format(type);
  }

  /**
   * returns future time when start < end
   * returns past time when start >= end
   */
  getRelativeTime(start: DateOrTimestamp, end: DateOrTimestamp): string {
    this.protectMultiThreadedRender();
    return dayjs(dayjs(start).utc()).to(dayjs(end).utc());
  }

  getRelativeTimeAbbr(start: DateOrTimestamp, end: DateOrTimestamp): string {
    this.protectMultiThreadedRender();
    return dayjs(dayjs(start).utc()).to(dayjs(end).utc(), true);
  }

  configureTimeExpression(options: TimeExpressionOptions): void {
    this.protectMultiThreadedRender();
    timeExpression.$i = false;
    dayjs.extend(timeExpression, options);
  }

  getTimeExpression(start: DateOrTimestamp, end: DateOrTimestamp): string {
    this.protectMultiThreadedRender();
    return dayjs.prototype.getTimeExpression(start, end);
  }

  private getDate(dateOrTimestamp: DateOrTimestamp): Date {
    if (typeof dateOrTimestamp === 'number') {
      return new Date(dateOrTimestamp);
    }
    return dateOrTimestamp;
  }

  /**
   * dayjs saves locale information on its prototype and returns a singleton class to us
   * With this design, the locale might be changed during multi-threaded rendering
   * To fix this, we save the current locale on BDDateFmt instance and check if we need to restore it
   * every time we call a function
   */
  private protectMultiThreadedRender(): void {
    const protoTypeLocale = dayjs.locale();
    if (this.currentLocale !== protoTypeLocale) {
      this.setLocale(this.currentLocale);
    }
  }
}

export default new BDDateFmt();
